import { useState } from 'react'
import PropTypes from 'prop-types'
import { useNavigate } from 'react-router-dom'
import { connect, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { renderHeaderIconContainer } from '@/components/Chat/ChatSidebar/InboxSelector'
import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'
import { useGetInboxesWithCustom } from '@/api/chat'

import { selectConversations, selectUnreadConversationIds } from '../../../../reducers/chatReducer'
import Icon from '../../../Icons'
import Avatar from '../../../../Avatar'
import DropdownDots from '../../../DropdownDots/DropdownDots'
import SelectField from '../../../FormLegacy/FormFieldsV2/SelectField'
import { updateChatClosedAtFirebase } from '../../../../firebase/privateChat/firebaseActions'
import {
  getDate,
  getProperTeamNameByType,
  smallChatOptions,
  conversationsFilters,
  getUserNameById,
  chatTypeToIconMap,
  conversationStatusMap,
} from '../../helpers'
import { cutTooLong } from '../../../../helpers/helpers'
import { getAssetLink } from '../../../../helpers/get-asset-link'

const ConversationsTab = ({
  handleBack,
  handleClose,
  handleOpenConversation,
  inbox,
  conversationsFilter,
  currentUser,
  conversations,
  unreadConversationIds,
  closedConversationsData,
  assignedConversationData,
  selectedBuilding,
  showAssigneeWindowForConversation,
  showFindChatWindow,
  availableManagementUsers,
  highManagementUsers,
}) => {
  const { t } = useTranslation(['chat'])
  const inboxType = inbox?.type ?? 'ALL'
  const [filter, setFilter] = useState(conversationsFilter)
  const navigate = useNavigate()

  const currentBuilding = useSelector(selectActiveBuilding)
  const { inboxMappings, checkIfCanSeeConversationByUserAndInboxType } = useGetInboxesWithCustom({
    buildingId: currentBuilding?.id,
    chatCustom: currentBuilding?.chatCustom,
  })

  const conversationsToDisplay = conversations
    .filter((conversation) =>
      checkIfCanSeeConversationByUserAndInboxType(conversation, currentUser, inbox),
    )
    .filter((conversation) => {
      switch (filter) {
        case 'open':
          return conversation.status === conversationStatusMap.OPEN
        case 'closed':
          return conversation.status === conversationStatusMap.CLOSED
        case 'archived':
          return conversation.status === conversationStatusMap.ARCHIVED
        case 'assignedToYou':
          return assignedConversationData.some(
            (assigned) =>
              assigned.assignee === currentUser.user.id &&
              assigned.name === conversation.channelName,
          )
        case 'unassigned':
          return !assignedConversationData
            .map((assigned) => assigned.name)
            .includes(conversation.channelName)
        default:
          return true
      }
    })
    .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))

  const isUnread = (conversation) => {
    return unreadConversationIds.includes(conversation.channelName)
  }

  const isClosed = (conversation) => {
    return closedConversationsData.find((conv) => conv.name === conversation.channelName)
  }

  const isAssigned = (conversation) => {
    const assignedIdObject = assignedConversationData.find(
      (assigned) => assigned.name === conversation.channelName,
    )
    const assignedUserData =
      assignedIdObject && availableManagementUsers
        ? availableManagementUsers.find((userObject) => userObject.id === assignedIdObject.assignee)
        : null

    return assignedUserData ? `${assignedUserData.firstName} ${assignedUserData.lastName}` : null
  }

  const onHandleCloseOrReopenConversation = (conversation) => {
    const isClosedAt = closedConversationsData.find(
      (conv) => conv.name === conversation.channelName,
    )
      ? null
      : new Date().toISOString()
    updateChatClosedAtFirebase(selectedBuilding.id, conversation.channelName, isClosedAt)
  }

  return (
    <div className="conversations-tab">
      <div className="tab-navbar">
        <div className="left-side">
          <div className="back-button" onClick={handleBack}>
            <Icon icon="arrow" />
          </div>
          <div className="inbox-data-container">
            {renderHeaderIconContainer(chatTypeToIconMap[inboxType])}
            <div className="inbox-data">
              <div className="inbox-name">
                {inbox?.name ?? t('chat:inboxSelector.unifiedInbox')}
              </div>
              <div className="inbox-phone">{t('chat:inboxSelector.allNumbers')}</div>
            </div>
          </div>
        </div>
        <div className="right-side">
          <div
            className="resize-button"
            onClick={() => navigate(`/dashboard/chat?queryInboxFilter=${inbox?.id ?? inboxType}`)}
          >
            <Icon icon="resize" viewBox={'0 0 25 20'} />
          </div>
          <div className="close-button" onClick={handleClose}>
            <Icon icon="cross" />
          </div>
        </div>
      </div>
      <div className="tab-body">
        <div className="navbar-action-components">
          <div className="search-button" onClick={showFindChatWindow}>
            {t('chat:conversationsTab.search')}
          </div>
          <SelectField
            name={'conversationAttributeFilter'}
            value={filter}
            placeholder={t('chat:conversationsTab.filterBy')}
            options={conversationsFilters}
            onChange={(e) => setFilter(e.target.value)}
            placeholderStyle={{ color: '#777777' }}
          />
        </div>
        <div className="conversations-list">
          {conversationsToDisplay.map((conv, index) => (
            <div
              key={index}
              className="conversation-row"
              onClick={() => handleOpenConversation(conv)}
            >
              <Avatar size="big">
                <img
                  src={getAssetLink({ path: 'chitown-avatar/avatar', file: conv.avatar })}
                  alt={''}
                />
              </Avatar>
              {isUnread(conv) && <div className="chat-notification-dot" />}
              <div className="conversation-info-wrap">
                <div className="first-info-row">
                  <div className="naming">{cutTooLong(conv.name, 30)}</div>
                  <div className="timing">{conv.lastMessage ? getDate(conv.updatedAt) : ''}</div>
                </div>
                <div className="second-info-row">
                  {conv.lastMessage ? (
                    <div className="last-message">
                      {`${getUserNameById(conv.lastMessageSenderIDfromTeam, conv, [
                        ...highManagementUsers,
                        ...availableManagementUsers,
                      ])}: ${conv.lastMessage || ''}`}
                    </div>
                  ) : (
                    ' '
                  )}
                </div>
                <div className="third-info-row">
                  <div className="conversation-team">
                    {conv.teamType === 'CUST'
                      ? inboxMappings.get(conv.channelName.split('_')[2])?.name ||
                        getProperTeamNameByType(conv.teamType)
                      : getProperTeamNameByType(conv.teamType)}
                  </div>
                  {conv.lastMessage ? (
                    <div
                      className={`conversation-message-type ${
                        conv.lastMessage.isSMS ? 'isSms' : 'isChat'
                      }`}
                    >
                      {conv.lastMessage.isSMS ? t('chat:toggle.sms') : t('chat:toggle.chat')}
                    </div>
                  ) : null}
                </div>
              </div>
              <DropdownDots
                options={smallChatOptions(
                  isClosed(conv),
                  isAssigned(conv),
                  () => showAssigneeWindowForConversation(conv),
                  () => onHandleCloseOrReopenConversation(conv),
                )}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

ConversationsTab.propTypes = {
  handleBack: PropTypes.func,
  handleClose: PropTypes.func,
  handleOpenConversation: PropTypes.func,
  inbox: PropTypes.object,
  conversationsFilter: PropTypes.string,
  currentUser: PropTypes.object,
  conversations: PropTypes.array,
  unreadConversationIds: PropTypes.array,
  closedConversationsData: PropTypes.array,
  selectedBuilding: PropTypes.object,
  assignedConversationData: PropTypes.array,
  showAssigneeWindowForConversation: PropTypes.func,
  showFindChatWindow: PropTypes.func,
  availableManagementUsers: PropTypes.array,
  highManagementUsers: PropTypes.array,
}

export default connect((state) => {
  return {
    conversations: selectConversations(state),
    unreadConversationIds: selectUnreadConversationIds(state),
  }
})(ConversationsTab)
